import React, { useState } from 'react';
import { X, Phone, Mail, FileText, User, Copy, Check } from 'lucide-react';
import { Agent } from '../types/Agent';

interface AgentModalProps {
  agent: Agent;
  onClose: () => void;
  onLearnMore?: (agent: Agent) => void;
}

const AgentModal: React.FC<AgentModalProps> = ({ agent, onClose, onLearnMore }) => {
  const [showEmailCopy, setShowEmailCopy] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePhoneClick = () => {
    window.open(`tel:${agent.phone}`, '_self');
  };

  const handleEmailClick = async () => {
    const subject = encodeURIComponent('Medicare Inquiry');
    const body = encodeURIComponent(`Hi ${agent.name},

I'd like to learn more about Medicare options. Please contact me at your earliest convenience.

Thank you!`);
    
    const mailtoLink = `mailto:${agent.email}?subject=${subject}&body=${body}`;
    
    // Try to open email client
    try {
      const link = document.createElement('a');
      link.href = mailtoLink;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      console.log('Email client failed to open');
    }
    
    // Always show the copy popup after a short delay (if mailto didn't work, user will see this)
    setTimeout(() => {
      setShowEmailCopy(true);
    }, 1000);
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(agent.email);
      setEmailCopied(true);
      setTimeout(() => {
        setEmailCopied(false);
        setShowEmailCopy(false);
      }, 2000);
    } catch {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = agent.email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setEmailCopied(true);
      setTimeout(() => {
        setEmailCopied(false);
        setShowEmailCopy(false);
      }, 2000);
    }
  };

  const handlePlanenrollClick = () => {
    window.open(agent.planenrollUrl, '_blank');
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4 py-8 animate-in fade-in duration-200"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="agent-modal-title"
      aria-describedby="agent-modal-description"
    >
      <div className="bg-white/95 backdrop-blur-sm rounded-xl max-w-sm w-full shadow-2xl border border-white/20 animate-in slide-in-from-bottom-4 zoom-in-95 duration-300">
        {/* Header */}
        <div className="relative p-3 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 bg-gray-100/80 hover:bg-bbha-plum-100 hover:text-bbha-plum-600 rounded-full p-2 transition-all duration-300 hover:scale-110"
            aria-label="Close agent contact modal"
          >
            <X size={18} className="text-gray-600" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 lg:p-5 space-y-3 lg:space-y-4">
          {/* Agent Info */}
          <div className="text-center space-y-2">
            <h2 id="agent-modal-title" className="text-xl lg:text-2xl font-bold text-gray-900">{agent.name}</h2>
            <p className="text-bbha-olive-600 font-semibold text-base lg:text-lg">{agent.title}</p>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">{agent.licenseNumber}</p>
            </div>
          </div>

          {/* Contact Actions */}
          <div className="space-y-2 lg:space-y-3">
            <h3 className="font-semibold text-gray-900 text-center text-base lg:text-lg">
              Connect with {agent.name.split(' ')[0]}
            </h3>
            
            {/* Enroll Now */}
            <button
              onClick={handlePlanenrollClick}
              className="w-full bg-gradient-to-r from-bbha-plum-800 to-bbha-plum-900 text-white py-4 lg:py-5 rounded-lg font-semibold hover:from-bbha-plum-900 hover:to-bbha-plum-950 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 text-base lg:text-lg"
              aria-label={`Enroll now with ${agent.name}`}
            >
              <FileText size={18} aria-hidden="true" />
              Enroll Now
            </button>

            {/* Phone */}
            <button
              onClick={handlePhoneClick}
              className="w-full bg-gradient-to-r from-bbha-plum-800 to-bbha-plum-900 text-white py-4 lg:py-5 rounded-lg font-semibold hover:from-bbha-plum-900 hover:to-bbha-plum-950 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 text-base lg:text-lg"
              aria-label={`Call ${agent.name} at ${agent.phone}`}
            >
              <Phone size={18} aria-hidden="true" />
              Call Now
            </button>

            {/* Email */}
            <button
              onClick={handleEmailClick}
              className="w-full bg-gradient-to-r from-bbha-plum-800 to-bbha-plum-900 text-white py-4 lg:py-5 rounded-lg font-semibold hover:from-bbha-plum-900 hover:to-bbha-plum-950 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 text-base lg:text-lg"
              aria-label={`Send email to ${agent.name} at ${agent.email}`}
            >
              <Mail size={18} aria-hidden="true" />
              Send Email
            </button>

            {/* Learn More */}
            {onLearnMore && (
              <button
                onClick={() => onLearnMore(agent)}
                className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white py-3 lg:py-4 rounded-lg font-medium hover:from-gray-700 hover:to-gray-800 transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95 text-sm lg:text-base"
                aria-label={`Learn more about ${agent.name}`}
              >
                <User size={16} aria-hidden="true" />
                Learn more about {agent.name.split(' ')[0]}
              </button>
            )}
          </div>

          {/* Additional Info */}
          <div id="agent-modal-description" className="bg-gradient-to-r from-gray-50 to-bbha-cream-50 rounded-lg p-3 text-center border border-gray-100">
            <p className="text-sm text-gray-600">
              All consultations are <span className="font-semibold text-bbha-olive-600">completely free</span> and there's no obligation to enroll.
            </p>
          </div>
        </div>
      </div>

      {/* Email Copy Popup */}
      {showEmailCopy && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl border border-gray-200 animate-in fade-in zoom-in-95 duration-300">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-bbha-olive-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-bbha-olive-600" />
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Contact {agent.name.split(' ')[0]}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Click to copy email address
                </p>
                
                <div className="bg-gray-50 rounded-lg p-3 mb-4 border border-gray-200">
                  <p className="text-sm font-mono text-gray-700 break-all">
                    {agent.email}
                  </p>
                </div>
              </div>

              <button
                onClick={handleCopyEmail}
                className="w-full bg-gradient-to-r from-bbha-olive-600 to-bbha-olive-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-bbha-olive-700 hover:to-bbha-olive-800 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {emailCopied ? (
                  <>
                    <Check size={18} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={18} />
                    Copy Email Address
                  </>
                )}
              </button>

              <button
                onClick={() => setShowEmailCopy(false)}
                className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentModal;
